import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import MuiAlert from "@mui/material/Alert";
import api from "../../services/api";

export default function DownloadPurchasesDialog({
  open,
  onClose,
  receipts = [],
}) {
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    setError(null);
    if (!startDate || !endDate) {
      setError("Please select both a start and end date.");
      return;
    }

    if (startDate.isAfter(endDate)) {
      setError("Start date cannot be after end date.");
      return;
    }

    setIsProcessing(true);

    try {
      // Filter purchases based on date range
      const filtered = receipts.filter((r) => {
        let d;
        if (r.receiptDate) {
          if (typeof r.receiptDate === "string") {
            const euMatch = r.receiptDate.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
            if (euMatch) {
              d = new Date(parseInt(euMatch[3], 10), parseInt(euMatch[2], 10) - 1, parseInt(euMatch[1], 10));
            } else {
              d = new Date(r.receiptDate);
            }
          } else if (r.receiptDate?.toDate) d = r.receiptDate.toDate();
          else if (r.receiptDate instanceof Date) d = r.receiptDate;
        } else if (r.uploadedAt) {
          if (r.uploadedAt?.toDate) d = r.uploadedAt.toDate();
          else d = new Date(r.uploadedAt);
        } else if (r.createdAt) {
          if (r.createdAt?.toDate) d = r.createdAt.toDate();
          else d = new Date(r.createdAt);
        }
        
        if (!d || isNaN(d)) return false;

        const rDate = dayjs(d);
        
        return (
          (rDate.isAfter(startDate, "day") || rDate.isSame(startDate, "day")) &&
          (rDate.isBefore(endDate, "day") || rDate.isSame(endDate, "day"))
        );
      });

      if (filtered.length === 0) {
        setError("No purchases found in the selected date range.");
        setIsProcessing(false);
        return;
      }

      const receiptIds = filtered.map((r) => r.id);

      const res = await api.post(
        "/receipts/zip",
        { receiptIds },
        { responseType: "blob" }
      );
      
      const zipFileName = `Purchases_${startDate.format("YYMMDD")}_to_${endDate.format("YYMMDD")}.zip`;
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = zipFileName;
      a.click();
      window.URL.revokeObjectURL(url);

      setTimeout(() => {
        setIsProcessing(false);
        onClose();
      }, 300);

    } catch (err) {
      console.error("Purchase zip error:", err);
      setError("An error occurred while building the archive. Please try again.");
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={isProcessing ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle fontWeight={700}>Download Purchases (ZIP)</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Select a date range. You will receive a ZIP containing the images of your purchases alongside a master `summary.csv` spreadsheet detailing every transaction.
        </Typography>

        {error && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box display="flex" flexDirection="column" gap={2}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              disabled={isProcessing}
              format="DD/MM/YYYY"
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              disabled={isProcessing}
              format="DD/MM/YYYY"
            />
          </Box>
        </LocalizationProvider>

        {isProcessing && (
          <Box mt={4} textAlign="center" display="flex" flexDirection="column" alignItems="center" gap={1}>
             <CircularProgress size={24} />
            <Typography variant="body2" fontWeight={600}>
              Building your ZIP...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} disabled={isProcessing} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          disabled={isProcessing}
          variant="contained"
          disableElevation
        >
          Download Archive
        </Button>
      </DialogActions>
    </Dialog>
  );
}
